import { ElementMetadata } from '../types';

export function findVisibleLabel(element: HTMLElement): string | undefined {
  if (element.id) {
    try {
      const label = document.querySelector(`label[for="${CSS.escape(element.id)}"]`);
      if (label?.textContent) return label.textContent.trim().slice(0, 50);
    } catch {
      // Invalid selector from special characters in element ID
    }
  }
  const parentLabel = element.closest('label');
  if (parentLabel?.textContent) return parentLabel.textContent.trim().slice(0, 50);
  const labelledBy = element.getAttribute('aria-labelledby');
  if (labelledBy) {
    const labelEl = document.getElementById(labelledBy);
    if (labelEl?.textContent) return labelEl.textContent.trim().slice(0, 50);
  }
  return undefined;
}

export function findParentSection(element: HTMLElement): string | undefined {
  let current = element.parentElement;
  while (current && current !== document.body) {
    const tagName = current.tagName.toLowerCase();
    if (['section', 'article', 'aside', 'nav', 'header', 'footer', 'main'].includes(tagName)) {
      const heading = current.querySelector('h1, h2, h3, h4, h5, h6');
      if (heading?.textContent) return `${tagName}: ${heading.textContent.trim().slice(0, 30)}`;
      return tagName;
    }
    const testId = current.getAttribute('data-testid');
    if (testId) return `[data-testid="${testId}"]`;
    if (current.id && !current.id.startsWith('ant-')) return `#${current.id}`;
    const role = current.getAttribute('role');
    if (role && ['dialog', 'main', 'navigation', 'complementary', 'form'].includes(role)) {
      return `[role="${role}"]`;
    }
    current = current.parentElement;
  }
  return undefined;
}

export function getElementMetadata(element: HTMLElement): ElementMetadata {
  const classes =
    Array.from(element.classList)
      .filter(
        (c) =>
          !c.startsWith('ant-') &&
          !c.startsWith('css-') &&
          c !== 'targeting-highlight' &&
          c !== 'targeting-locked',
      )
      .slice(0, 3)
      .join(' ') || undefined;

  return {
    element_type: element.tagName.toLowerCase(),
    element_id: element.id && !element.id.startsWith('ant-') ? element.id : undefined,
    element_text: element.textContent?.trim().slice(0, 50) || undefined,
    data_testid: element.getAttribute('data-testid') || undefined,
    element_name: element.getAttribute('name') || undefined,
    placeholder: element.getAttribute('placeholder') || undefined,
    aria_label: element.getAttribute('aria-label') || undefined,
    element_class: classes,
    parent_section: findParentSection(element),
    visible_label: findVisibleLabel(element),
  };
}
